import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Clock, Coffee, LogIn, LogOut, MessageSquareWarning, CheckCircle2 } from "lucide-react";

const fmt = (m) => `${Math.floor(m / 60)}h ${Math.round(m % 60)}m`;
const fmtTime = (s) => (s ? new Date(s).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—");

export default function TimeClockPage() {
  const { isAdmin } = useAuth();
  const [entries, setEntries] = useState([]);
  const [current, setCurrent] = useState(null);
  const [now, setNow] = useState(new Date());
  const [dialog, setDialog] = useState(null);
  const [justification, setJustification] = useState("");

  useEffect(() => { const i = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(i); }, []);

  const load = async () => {
    const [r1, r2] = await Promise.all([
      api.get("/timeclock/mine?limit=50"),
      api.get("/timeclock/current"),
    ]);
    setEntries(r1.data);
    setCurrent(r2.data);
  };
  useEffect(() => { load(); }, []);

  const doAction = async (action) => {
    try {
      await api.post(`/timeclock/action?action=${action}`);
      toast.success("Erfasst");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler");
    }
  };

  const submitJustification = async () => {
    try {
      await api.post("/timeclock/justify", { entry_id: dialog.id, justification });
      toast.success("Begründung gespeichert");
      setDialog(null);
      setJustification("");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler");
    }
  };

  const onBreak = current?.breaks?.length && !current.breaks[current.breaks.length - 1].end;

  return (
    <div className="space-y-6" data-testid="timeclock-root">
      <Card className="border-border/60">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading text-xl">Aktuelle Sitzung</CardTitle>
            <div className="font-heading text-4xl font-extrabold tabular-nums">
              {now.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {current ? (
            <div className="flex items-center gap-3 mb-4 text-sm">
              <div className={`h-2 w-2 rounded-full ${onBreak ? "bg-amber-500" : "bg-emerald-500"} animate-pulse`} />
              <span className="text-slate-700 dark:text-slate-300">
                {onBreak ? "In Pause" : "Eingestempelt"} · seit {fmtTime(current.clock_in)}
              </span>
            </div>
          ) : (
            <div className="mb-4 text-sm text-slate-500">Noch nicht eingestempelt.</div>
          )}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <Button data-testid="tc-in" onClick={() => doAction("in")} disabled={!!current} className="rounded-full bg-emerald-600 hover:bg-emerald-700 h-11">
              <LogIn className="w-4 h-4 mr-2" /> Kommen
            </Button>
            <Button data-testid="tc-break-start" onClick={() => doAction("break_start")} disabled={!current || onBreak} variant="outline" className="rounded-full h-11">
              <Coffee className="w-4 h-4 mr-2" /> Pause an
            </Button>
            <Button data-testid="tc-break-end" onClick={() => doAction("break_end")} disabled={!onBreak} variant="outline" className="rounded-full h-11">
              <Coffee className="w-4 h-4 mr-2" /> Pause aus
            </Button>
            <Button data-testid="tc-out" onClick={() => doAction("out")} disabled={!current} className="rounded-full bg-rose-600 hover:bg-rose-700 h-11">
              <LogOut className="w-4 h-4 mr-2" /> Gehen
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="border-border/60">
        <CardHeader>
          <CardTitle className="font-heading text-xl">Meine Zeiterfassung</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Datum</TableHead>
                  <TableHead>Beginn</TableHead>
                  <TableHead>Ende</TableHead>
                  <TableHead>Pause</TableHead>
                  <TableHead>Netto</TableHead>
                  <TableHead>Überstd.</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.map((e) => (
                  <TableRow key={e.id} data-testid={`tc-row-${e.id}`}>
                    <TableCell className="font-medium">{e.date}</TableCell>
                    <TableCell>{fmtTime(e.clock_in)}</TableCell>
                    <TableCell>{fmtTime(e.clock_out)}</TableCell>
                    <TableCell>{Math.round(e.break_minutes || 0)}m</TableCell>
                    <TableCell className="font-semibold">{fmt(e.net_minutes || 0)}</TableCell>
                    <TableCell>
                      {(e.overtime_minutes || 0) > 0 ? (
                        <Badge variant="outline" className="text-amber-700 border-amber-300">{fmt(e.overtime_minutes)}</Badge>
                      ) : "—"}
                    </TableCell>
                    <TableCell>
                      {e.status === "open" ? <Badge variant="secondary">Offen</Badge> :
                        e.over_buffer && !e.justification ? (
                          <Button data-testid={`tc-justify-${e.id}`} size="sm" variant="outline" onClick={() => setDialog(e)}>
                            <MessageSquareWarning className="w-3 h-3 mr-1" /> Begründen
                          </Button>
                        ) : e.justification ? (
                          <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100"><CheckCircle2 className="w-3 h-3 mr-1" /> Begründet</Badge>
                        ) : (
                          <Badge variant="outline">OK</Badge>
                        )}
                    </TableCell>
                  </TableRow>
                ))}
                {entries.length === 0 && (
                  <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-8">Noch keine Einträge</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={!!dialog} onOpenChange={() => setDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Überstunden begründen</DialogTitle></DialogHeader>
          <div className="text-sm text-slate-500 mb-3">
            {dialog?.date} · {fmt(dialog?.overtime_minutes || 0)} über Puffer
          </div>
          <Textarea
            data-testid="tc-justify-input"
            placeholder="Grund (z. B. Kundenandrang im Notdienst)"
            value={justification}
            onChange={(e) => setJustification(e.target.value)}
            rows={4}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Abbrechen</Button>
            <Button data-testid="tc-justify-save" onClick={submitJustification}>Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
